import { AbsurdIpfError } from '@ipfabric/frontend-shared-assets/utils/IpfError';
import Joi, { type Schema } from 'joi';
import { removeDuplicates } from '../../utils/removeDuplicates';
import type {
  IPSubnetInputType,
  ValidationResult,
} from '../IPSubnetInput.types';

export const makeJoiPredicate =
  (schema: Schema) =>
  (value: unknown): boolean => {
    try {
      return !schema.validate(value).error;
    } catch {
      return false;
    }
  };

const cidrIpv4Schema = Joi.string().ip({
  cidr: 'required',
  version: ['ipv4'],
});

const ipv4Schema = Joi.string().ip({
  cidr: 'forbidden',
  version: ['ipv4'],
});

const cidrIpv6Schema = Joi.string().ip({
  cidr: 'required',
  version: ['ipv6'],
});

const ipv6Schema = Joi.string().ip({
  cidr: 'forbidden',
  version: ['ipv6'],
});

const ipWithOptionalCidrValidationSchema = Joi.alternatives().try(
  cidrIpv4Schema,
  ipv4Schema,
  cidrIpv6Schema,
  ipv6Schema,
);
const ipWithRequiredCidrValidationSchema = Joi.alternatives().try(
  cidrIpv4Schema,
  cidrIpv6Schema,
);
const ipWithForbiddenCirdValidationSchema = Joi.alternatives().try(
  ipv4Schema,
  ipv6Schema,
);

const validateIPAddresses = (schema: Joi.Schema) => (addresses: string[]) => {
  const invalidAddresses: string[] = [];
  let message = undefined;
  for (const address of addresses) {
    const { error } = schema.validate(address);
    if (error) {
      invalidAddresses.push(address);
      message =
        addresses.length > 1
          ? 'Some addresses are not in a valid format.'
          : 'Address is not in a valid format.';
    }
  }

  return {
    message,
    validAddresses: addresses.filter(
      (address) => !invalidAddresses.includes(address),
    ),
    invalidAddresses: invalidAddresses,
  };
};
const validateIpWithOptionalCidr = (value: string[]) =>
  validateIPAddresses(ipWithOptionalCidrValidationSchema)(value);
const validateIpWithRequiredCidr = (value: string[]) =>
  validateIPAddresses(ipWithRequiredCidrValidationSchema)(value);
const validateIpWithForbiddenCidr = (value: string[]) =>
  validateIPAddresses(ipWithForbiddenCirdValidationSchema)(value);

export const validateByType = (value: string[], type: IPSubnetInputType) => {
  switch (type) {
    case 'ip':
      return validateIpWithForbiddenCidr(value);
    case 'cidr':
      return validateIpWithRequiredCidr(value);
    case 'ip/cidr':
      return validateIpWithOptionalCidr(value);
    default:
      throw new AbsurdIpfError(type, 'unknown IPSubnetInput type');
  }
};

export const mergeValidationResults = (
  builtInValidationResult: ValidationResult,
  customValidationResult?: ValidationResult,
): typeof builtInValidationResult => {
  if (!customValidationResult) {
    return builtInValidationResult;
  }

  const validAddresses = removeDuplicates([
    ...builtInValidationResult.validAddresses.filter(
      (validAddress) =>
        !customValidationResult.invalidAddresses.includes(validAddress),
    ),
    ...customValidationResult.validAddresses,
  ]);
  const invalidAddresses = removeDuplicates([
    ...builtInValidationResult.invalidAddresses,
    ...customValidationResult.invalidAddresses,
  ]);
  return {
    message: builtInValidationResult.message || customValidationResult.message,
    validAddresses: validAddresses,
    invalidAddresses: invalidAddresses,
  };
};

export const isIPv6 = makeJoiPredicate(ipv6Schema);
export const isIPv6CIDR = makeJoiPredicate(cidrIpv6Schema);
export const isValidIPv6 = (value: unknown) =>
  isIPv6(value) || isIPv6CIDR(value);

export const isIPv4 = makeJoiPredicate(ipv4Schema);
export const isIPv4CIDR = makeJoiPredicate(cidrIpv4Schema);
export const isValidIPv4 = (value: unknown) =>
  isIPv4(value) || isIPv4CIDR(value);
